<template>
  <div class="pg-wrap" ref="wrap">
    <div class="pg-topbar">
      <div class="pg-top-left">
        <span class="pg-icon">🏓</span>
        <span class="pg-game-title">Pong</span>
      </div>
      <div class="pg-scoreboard">
        <div class="pg-score-block pg-you-block">
          <span class="pg-score-val pg-you">{{ playerScore }}</span>
          <span class="pg-score-lbl">you</span>
        </div>
        <span class="pg-sep">:</span>
        <div class="pg-score-block pg-ai-block">
          <span class="pg-score-val pg-ai">{{ aiScore }}</span>
          <span class="pg-score-lbl">VIKI</span>
        </div>
      </div>
      <span class="pg-to-win">First to {{ winScore }}</span>
    </div>

    <div class="pg-board-wrap" ref="boardWrap">
      <canvas ref="canvas" class="pg-canvas"
        @mousemove="onMouseMove"
        @touchmove.prevent="onTouch"
      ></canvas>

      <div v-if="!running && !over" class="pg-overlay">
        <div class="pg-ov-card">
          <div class="pg-ov-emoji">🏓</div>
          <h2 class="pg-ov-title">Pong</h2>
          <p class="pg-ov-sub">Mouse / touch / W-S keys</p>
          <button class="pg-btn" @click="startGame">▶ Start</button>
        </div>
      </div>

      <div v-if="over" class="pg-overlay">
        <div class="pg-ov-card">
          <div class="pg-ov-emoji">{{ playerScore >= winScore ? '🏆' : '😞' }}</div>
          <h2 class="pg-ov-title">{{ playerScore >= winScore ? 'You Win!' : 'VIKI Wins!' }}</h2>
          <div class="pg-final">{{ playerScore }} : {{ aiScore }}</div>
          <button class="pg-btn" @click="startGame">↺ Play Again</button>
        </div>
      </div>
    </div>

    <!-- mobile controls -->
    <div class="pg-mobile-ctrl">
      <button class="pg-ctrl-btn"
        @mousedown="padDir=-1" @touchstart.prevent="padDir=-1"
        @mouseup="padDir=0" @touchend.prevent="padDir=0">▲ Up</button>
      <button class="pg-ctrl-btn"
        @mousedown="padDir=1" @touchstart.prevent="padDir=1"
        @mouseup="padDir=0" @touchend.prevent="padDir=0">▼ Down</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PongGame1',
  data() {
    return {
      running: false,
      over: false,
      playerScore: 0,
      aiScore: 0,
      winScore: 5,
      padDir: 0,
      loop: null,
      W: 0, H: 0,
      pad: { y: 0, h: 0, w: 14 },
      aiPad: { y: 0, h: 0, w: 14 },
      ball: { x: 0, y: 0, vx: 0, vy: 0, r: 8 },
      ctx: null,
    };
  },
  mounted() {
    this.$nextTick(() => this.initCanvas());
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('keyup', this.onKeyUp);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('keyup', this.onKeyUp);
    if (this.loop) cancelAnimationFrame(this.loop);
  },
  methods: {
    initCanvas() {
      const bw = this.$refs.boardWrap;
      const cv = this.$refs.canvas;
      if (!bw || !cv) return;
      this.W = bw.clientWidth || 500;
      this.H = bw.clientHeight || 300;
      cv.width = this.W;
      cv.height = this.H;
      this.ctx = cv.getContext('2d');
      this.pad.h = this.H * 0.22;
      this.aiPad.h = this.H * 0.20;
      this.pad.y = this.H / 2 - this.pad.h / 2;
      this.aiPad.y = this.H / 2 - this.aiPad.h / 2;
      this.drawIdle();
    },
    onResize() { this.initCanvas(); },
    drawIdle() {
      const c = this.ctx; if (!c) return;
      // light court background
      c.fillStyle = '#f0f4ff';
      c.fillRect(0, 0, this.W, this.H);
      // court lines
      c.strokeStyle = 'rgba(99,102,241,0.15)';
      c.lineWidth = 1;
      c.strokeRect(20, 10, this.W - 40, this.H - 20);
      // center dashes
      c.setLineDash([10, 10]);
      c.strokeStyle = 'rgba(99,102,241,0.2)';
      c.lineWidth = 2;
      c.beginPath(); c.moveTo(this.W / 2, 10); c.lineTo(this.W / 2, this.H - 10); c.stroke();
      c.setLineDash([]);
    },
    startGame() {
      this.over = false;
      this.running = true;
      this.playerScore = 0;
      this.aiScore = 0;
      this.resetBall(1);
      if (this.loop) cancelAnimationFrame(this.loop);
      this.gameLoop();
    },
    resetBall(dir) {
      this.ball.x = this.W / 2;
      this.ball.y = this.H / 2;
      const angle = (Math.random() * 50 - 25) * Math.PI / 180;
      const speed = this.W * 0.0075;
      this.ball.vx = Math.cos(angle) * speed * dir;
      this.ball.vy = Math.sin(angle) * speed;
    },
    gameLoop() {
      if (!this.running) return;
      this.update();
      this.draw();
      this.loop = requestAnimationFrame(() => this.gameLoop());
    },
    update() {
      const b = this.ball;
      const padSpeed = this.H * 0.013;

      if (this.padDir !== 0) {
        this.pad.y = Math.max(0, Math.min(this.H - this.pad.h, this.pad.y + this.padDir * padSpeed));
      }

      // VIKI tracking
      const aiCenter = this.aiPad.y + this.aiPad.h / 2;
      const diff = b.y - aiCenter;
      const aiSpeed = Math.min(Math.abs(diff), padSpeed * 0.88);
      this.aiPad.y += Math.sign(diff) * aiSpeed;
      this.aiPad.y = Math.max(0, Math.min(this.H - this.aiPad.h, this.aiPad.y));

      b.x += b.vx;
      b.y += b.vy;

      // walls
      if (b.y - b.r < 10) { b.y = 10 + b.r; b.vy = Math.abs(b.vy); }
      if (b.y + b.r > this.H - 10) { b.y = this.H - 10 - b.r; b.vy = -Math.abs(b.vy); }

      const padX = 20 + this.pad.w;
      const aiX = this.W - 20 - this.aiPad.w;

      // player hit
      if (b.x - b.r < padX && b.vx < 0 && b.y > this.pad.y && b.y < this.pad.y + this.pad.h) {
        b.x = padX + b.r;
        const relY = (b.y - this.pad.y) / this.pad.h - 0.5;
        b.vy = relY * this.W * 0.013;
        b.vx = Math.abs(b.vx) * 1.04;
      }
      // VIKI hit
      if (b.x + b.r > aiX && b.vx > 0 && b.y > this.aiPad.y && b.y < this.aiPad.y + this.aiPad.h) {
        b.x = aiX - b.r;
        const relY = (b.y - this.aiPad.y) / this.aiPad.h - 0.5;
        b.vy = relY * this.W * 0.013;
        b.vx = -Math.abs(b.vx) * 1.04;
      }

      // score
      if (b.x < 0) { this.aiScore++; this.$emit('score-update', this.playerScore); this.checkWin() || this.resetBall(1); }
      if (b.x > this.W) { this.playerScore++; this.$emit('score-update', this.playerScore); this.checkWin() || this.resetBall(-1); }
    },
    checkWin() {
      if (this.playerScore >= this.winScore || this.aiScore >= this.winScore) {
        this.running = false;
        this.over = true;
        cancelAnimationFrame(this.loop);
        this.$emit('game-over', this.playerScore);
        return true;
      }
      return false;
    },
    draw() {
      const c = this.ctx; if (!c) return;

      // court bg
      const bgGrad = c.createLinearGradient(0, 0, 0, this.H);
      bgGrad.addColorStop(0, '#f0f4ff');
      bgGrad.addColorStop(1, '#e8eeff');
      c.fillStyle = bgGrad;
      c.fillRect(0, 0, this.W, this.H);

      // court border
      c.strokeStyle = 'rgba(99,102,241,0.12)';
      c.lineWidth = 1;
      c.strokeRect(20, 10, this.W - 40, this.H - 20);

      // center line
      c.setLineDash([10, 10]);
      c.strokeStyle = 'rgba(99,102,241,0.18)';
      c.lineWidth = 2;
      c.beginPath(); c.moveTo(this.W / 2, 10); c.lineTo(this.W / 2, this.H - 10); c.stroke();
      c.setLineDash([]);

      // player pad - purple gradient
      const pg = c.createLinearGradient(20, this.pad.y, 20 + this.pad.w, this.pad.y + this.pad.h);
      pg.addColorStop(0, '#7c5cfc');
      pg.addColorStop(1, '#5b21b6');
      c.fillStyle = pg;
      this.roundRect(c, 20, this.pad.y, this.pad.w, this.pad.h, 7);
      // pad glow
      c.shadowColor = 'rgba(124,92,252,0.5)';
      c.shadowBlur = 12;
      c.fillStyle = pg;
      this.roundRect(c, 20, this.pad.y, this.pad.w, this.pad.h, 7);
      c.shadowBlur = 0;

      // VIKI pad - coral/red gradient
      const ag = c.createLinearGradient(this.W - 20 - this.aiPad.w, this.aiPad.y, this.W - 20, this.aiPad.y + this.aiPad.h);
      ag.addColorStop(0, '#f97316');
      ag.addColorStop(1, '#dc2626');
      c.fillStyle = ag;
      c.shadowColor = 'rgba(239,68,68,0.5)';
      c.shadowBlur = 12;
      this.roundRect(c, this.W - 20 - this.aiPad.w, this.aiPad.y, this.aiPad.w, this.aiPad.h, 7);
      c.shadowBlur = 0;

      // ball with glow
      c.shadowColor = 'rgba(99,102,241,0.6)';
      c.shadowBlur = 18;
      const ballGrad = c.createRadialGradient(this.ball.x - 2, this.ball.y - 2, 1, this.ball.x, this.ball.y, this.ball.r);
      ballGrad.addColorStop(0, '#fff');
      ballGrad.addColorStop(0.5, '#c7d2fe');
      ballGrad.addColorStop(1, '#818cf8');
      c.beginPath();
      c.arc(this.ball.x, this.ball.y, this.ball.r, 0, Math.PI * 2);
      c.fillStyle = ballGrad;
      c.fill();
      c.shadowBlur = 0;
    },
    roundRect(c, x, y, w, h, r) {
      c.beginPath();
      c.moveTo(x + r, y);
      c.lineTo(x + w - r, y);
      c.arcTo(x + w, y, x + w, y + r, r);
      c.lineTo(x + w, y + h - r);
      c.arcTo(x + w, y + h, x + w - r, y + h, r);
      c.lineTo(x + r, y + h);
      c.arcTo(x, y + h, x, y + h - r, r);
      c.lineTo(x, y + r);
      c.arcTo(x, y, x + r, y, r);
      c.closePath();
      c.fill();
    },
    onMouseMove(e) {
      if (!this.running) return;
      const rect = this.$refs.canvas.getBoundingClientRect();
      const y = e.clientY - rect.top;
      this.pad.y = Math.max(0, Math.min(this.H - this.pad.h, y - this.pad.h / 2));
    },
    onTouch(e) {
      if (!this.running) return;
      const rect = this.$refs.canvas.getBoundingClientRect();
      const y = e.touches[0].clientY - rect.top;
      this.pad.y = Math.max(0, Math.min(this.H - this.pad.h, y - this.pad.h / 2));
    },
    onKey(e) {
      if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') this.padDir = -1;
      if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S') this.padDir = 1;
    },
    onKeyUp(e) {
      if (['ArrowUp', 'ArrowDown', 'w', 'W', 's', 'S'].includes(e.key)) this.padDir = 0;
    },
  },
};
</script>

<style scoped>
.pg-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;
  min-height: 380px;
}

.pg-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  border-bottom: 1px solid #c7d2fe;
  flex-shrink: 0;
}
.pg-top-left { display: flex; align-items: center; gap: 6px; }
.pg-icon { font-size: 1.1rem; }
.pg-game-title { font-size: .95rem; font-weight: 800; color: #4338ca; }

.pg-scoreboard {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pg-score-block { display: flex; flex-direction: column; align-items: center; }
.pg-score-val { font-size: 1.4rem; font-weight: 900; line-height: 1; }
.pg-score-lbl { font-size: .6rem; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; }
.pg-you { color: #7c5cfc; }
.pg-ai { color: #f97316; }
.pg-you-block .pg-score-lbl { color: #a5b4fc; }
.pg-ai-block .pg-score-lbl { color: #fdba74; }
.pg-sep { font-size: 1.2rem; color: #c7d2fe; font-weight: 900; }
.pg-to-win { font-size: .72rem; color: #a5b4fc; font-weight: 600; }

.pg-board-wrap {
  flex: 1;
  position: relative;
  min-height: 0;
  background: #f0f4ff;
}
.pg-canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.pg-overlay {
  position: absolute;
  inset: 0;
  background: rgba(240,244,255,0.92);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pg-ov-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1.5px solid #c7d2fe;
  border-radius: 20px;
  padding: 28px 36px;
  box-shadow: 0 8px 40px rgba(99,102,241,0.14);
}
.pg-ov-emoji { font-size: 3rem; }
.pg-ov-title { font-size: 1.4rem; font-weight: 800; color: #4338ca; margin: 0; }
.pg-ov-sub { font-size: .75rem; color: #a5b4fc; margin: 0; }
.pg-final { font-size: 2.2rem; font-weight: 900; color: #7c5cfc; }
.pg-btn {
  margin-top: 4px;
  background: linear-gradient(135deg, #7c5cfc, #4338ca);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 28px;
  font-size: .9rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(99,102,241,0.35);
  transition: transform .15s, box-shadow .15s;
}
.pg-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(99,102,241,0.45); }

.pg-mobile-ctrl {
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 10px;
  background: linear-gradient(180deg, #f9fafb, #f3f4f6);
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.pg-ctrl-btn {
  padding: 10px 28px;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  color: #374151;
  font-size: .88rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.06);
  transition: all .1s;
}
.pg-ctrl-btn:hover { background: #eef2ff; border-color: #7c5cfc; color: #4338ca; }
.pg-ctrl-btn:active { transform: scale(0.95); background: #e0e7ff; }
</style>
